// OPEN — the project's "what's still unresolved" working surface. Standalone
// fourth column on the right of the workspace. Was previously labelled GAPS
// but the column carries more than gap-kind cards (stale ripple, frame gaps),
// and `gap` is also a card kind — the name overlap was confusing. Renamed to
// OPEN: the natural complement of the cards column ("settled"). The file is
// still GapLedger.tsx for git-history continuity.
//
// ── Sections (top-to-bottom) ───────────────────────────────────────
//
// 1. OPEN QUESTIONS — every kind=question card that isn't archived.
//    Questions live here (not in the cards column) post-redesign because
//    they're inherently unresolved — the cards column is for settled
//    knowledge (statement/decision/observation/note). Each row offers
//    `[answer ↗]` (pin to discussion + open thread) and `[archive]`
//    (soft-hide once the user judges it answered or no longer relevant).
//
// 2. PENDING REVIEW — every gap card. Each gap is a proposal awaiting user
//    resolution. The Approve button's label is smart: `Add to OPEN` when
//    `proposed_kind=question` (it'll join section 1), `Add to cards`
//    otherwise. When the gap carries `extra.priority`, proposals group by
//    p0/p1/p2/p3.
//
// 3. STALE RIPPLE — non-gap cards with stale=true. Surfaced when a decision
//    is re-asserted and the propagation flags downstream cards as needing
//    review. The cards themselves aren't gaps but they need the user's eye.
//
// 4. FRAME GAPS — empty `mece_required` nodes in the active frame.
//    Lifted up from CardsPanel's inline banner via App's frameGaps state.
//
// Per doc/03 §1.2 a gap card carries:
//   - extra.gap_kind: interpretation_needed (legacy: fact_needed / decision / conflict)
//   - extra.proposed_kind: kind it would become on Approve (statement, decision, …)
//   - extra.ambiguity_reason: one-sentence user-readable explanation
//   - extra.priority: optional p0/p1/p2/p3 (audit/decompose distill fills this)
//   - extra.source:   optional "audit" | "decompose" | "rescue" | "chat"
//                     (where the gap came from; rendered as a chip)
//   - extra.blocks:   [card_id, …]   (optional; rejecting writes
//                     `dismissed_gaps` entries to these targets)

import { useDeferredValue, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Card } from '../api';
import { CardBodyMarkdown } from './cards/CardBodyMarkdown';
import type { FrameGapInfo } from './CardsPanel';
import { TipsTicker } from './TipsTicker';
import * as perf from '../utils/switch_perf';

export interface GapLedgerProps {
  cards: Card[];
  /** Single-click on a row or a blocks chip jumps to the referenced card. */
  onSelect: (id: string) => void;
  /** Approve a proposal — promote the gap to its proposed_kind. The handler
   *  is responsible for the API call + optimistic update + error toast. */
  onApproveProposal?: (cardId: string) => Promise<void> | void;
  /** Reject a proposal — delete the gap card and write `dismissed_gaps`
   *  entries to its blocks targets. */
  onRejectProposal?: (cardId: string) => Promise<void> | void;
  /** Single-click on a proposal row opens CardPeek in view mode anchored to
   *  the row's rect — same wiring as CardsPanel's double-click peek. The
   *  peek's view-mode footer carries the "edit" affordance, so editing a
   *  proposal lives inside the preview rather than as a separate row button. */
  onPeek?: (cardId: string, anchor: DOMRect) => void;
  /** Pin this gap to the DiscussionPane and steer the conversation toward
   *  resolving it. The pinned gap is sent to the agent as a hint until the
   *  user clicks Resolve in DiscussionPane. */
  onDiscussProposal?: (cardId: string) => void;
  /** OPEN QUESTIONS row [answer ↗] button — pins the question to a fresh
   *  thread so the user can work through it with the agent. Same wiring
   *  as `onDiscussProposal` (App points both at the same handler), kept
   *  separate so the GapLedger can disable it without affecting gap
   *  proposals. */
  onAnswerQuestion?: (cardId: string) => void;
  /** OPEN QUESTIONS row [archive] button — soft-hides the question. The
   *  card stays in the DB so wikilinks resolve; it just stops appearing
   *  in this section. Used when the user judges the question answered or
   *  no longer relevant. */
  onArchiveQuestion?: (cardId: string) => void;
  /** Hovering a row that has `extra.context.turns` calls this with the
   *  cited turn ids; the parent passes them to DiscussionPane to draw
   *  amber outlines on matching bubbles. mouseleave fires with `[]`. */
  onHoverTurns?: (turnIds: string[]) => void;
  /** Click on a single cited turn id ("t-19") asks the parent to scroll
   *  the discussion to that bubble. Hover only highlights — jump is the
   *  intentional path. */
  onJumpToTurn?: (turnId: string) => void;
  /** Active frame's MECE gaps (empty `mece_required` nodes). Surfaced from
   *  CardsPanel via App's `onFrameGapsChange` callback. */
  frameGaps?: FrameGapInfo[];
  /** Click on a frame gap row → opens the same probe popover the inline
   *  MeceBanner in CardsPanel does. */
  onFrameGapClick?: (gap: FrameGapInfo) => void;
  /** Trigger a project-wide gap audit — opens the distill modal in `audit`
   *  mode and fans out specific gaps into OPEN. Disabled when no thread is
   *  active or another distill is mid-flight. */
  onAuditGaps?: () => void;
  /** True iff a thread is selected (audit needs one to write gaps to). */
  canAudit?: boolean;
  /** True while a distill / audit is in flight. Greys the audit button. */
  auditing?: boolean;
}

const COLUMN_WIDTH = 280;
/** Auto-widen threshold: when proposals + stale + frame ≥ this, the column
 *  expands so OPEN becomes the primary working surface. */
const WIDE_THRESHOLD = 5;
const COLUMN_WIDTH_WIDE = 360;
const COLLAPSED_WIDTH = 32;

export function GapLedger({
  cards,
  onSelect,
  onApproveProposal,
  onRejectProposal,
  onPeek,
  onDiscussProposal,
  onAnswerQuestion,
  onArchiveQuestion,
  onHoverTurns,
  onJumpToTurn,
  frameGaps = [],
  onFrameGapClick,
  onAuditGaps,
  canAudit = false,
  auditing = false,
}: GapLedgerProps) {
  const { t } = useTranslation();
  perf.renderMark('GapLedger', { cards: cards.length });
  // Defer the per-card iteration + render so a project switch's chrome
  // commits in the same frame as the click. Same rationale as CardsPanel.
  // Guard against the deferred-lag window where useDeferredValue is
  // still returning the previous project's cards: if the prop's
  // project_id doesn't match the deferred value's, render empty for
  // the one extra frame until it catches up.
  const deferredCardsRaw = useDeferredValue(cards);
  const deferredCards = useMemo(() => {
    if (cards.length === 0 || deferredCardsRaw.length === 0) return deferredCardsRaw;
    if (cards[0].project_id === deferredCardsRaw[0].project_id) return deferredCardsRaw;
    return [];
  }, [cards, deferredCardsRaw]);
  const proposals: Card[] = [];
  const openQuestions: Card[] = [];
  const staleCards: Card[] = [];

  for (const c of deferredCards) {
    // Skip archived cards across all sections — they're soft-hidden by
    // the user (or by `onArchiveQuestion` for resolved questions). They
    // still exist in the DB so wikilinks resolve, but OPEN should reflect
    // "what's currently demanding attention", not history.
    if (c.archived) continue;
    if (c.kind === 'gap') {
      proposals.push(c);
    } else if (c.kind === 'question') {
      openQuestions.push(c);
    } else if (c.stale) {
      staleCards.push(c);
    }
  }
  // Newest-first within OPEN QUESTIONS — easier to scan a recent audit
  // burst from the top. Stable tiebreak by card_id so two questions with
  // the same created_at don't reshuffle on every render.
  openQuestions.sort((a, b) => {
    const cmp = (b.created_at ?? '').localeCompare(a.created_at ?? '');
    return cmp !== 0 ? cmp : b.card_id.localeCompare(a.card_id);
  });

  // Project-wide known ids — drives wikilink rendering inside proposal
  // bodies (`[[c-X]]` becomes a clickable jump link only when the id is
  // in this set; unknown ids stay as plain text).
  const knownIds = useMemo(
    () => new Set(deferredCards.map((c) => c.card_id)),
    [deferredCards],
  );

  const totalCount =
    openQuestions.length + proposals.length + staleCards.length + frameGaps.length;

  // null = no manual override → fall back to "open iff non-empty". Once the
  // user clicks the chevron we lock in their choice for the session, so a
  // gap appearing or disappearing doesn't yank the panel open or shut on them.
  const [explicitOpen, setExplicitOpen] = useState<boolean | null>(null);
  const isOpen = explicitOpen ?? totalCount > 0;

  if (!isOpen) {
    return <CollapsedRail count={totalCount} onExpand={() => setExplicitOpen(true)} />;
  }

  const wide = totalCount >= WIDE_THRESHOLD;
  // Group proposals by priority when any of them carry one. p0..p3 first,
  // then "—" (no priority) at the end. Inside a bucket, preserve the
  // server-given order (which the audit prompt asks the model to sort).
  const anyPriority = proposals.some((g) => readPriority(g));
  const proposalGroups = anyPriority ? groupByPriority(proposals) : null;

  return (
    <aside
      style={{
        width: wide ? COLUMN_WIDTH_WIDE : COLUMN_WIDTH,
        flexShrink: 0,
        height: '100%',
        background: 'var(--paper-1)',
        borderLeft: '1px solid var(--rule)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        transition: 'width .2s',
      }}
    >
      <Header
        count={totalCount}
        onCollapse={() => setExplicitOpen(false)}
        onAuditGaps={onAuditGaps}
        canAudit={canAudit}
        auditing={auditing}
      />
      {totalCount === 0 ? (
        <div style={{ flex: 1, display: 'flex' }}>
          <EmptyState onAuditGaps={onAuditGaps} canAudit={canAudit} auditing={auditing} />
        </div>
      ) : (
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 12px 18px' }}>
          {openQuestions.length > 0 && (
            <Section
              title={t('gaps.sectionOpenQuestions')}
              count={openQuestions.length}
              tone="ink"
              defaultOpen
            >
              {openQuestions.map((q) => (
                <OpenQuestionRow
                  key={q.card_id}
                  card={q}
                  knownIds={knownIds}
                  onPeek={onPeek}
                  onSelect={onSelect}
                  onAnswer={onAnswerQuestion}
                  onArchive={onArchiveQuestion}
                />
              ))}
            </Section>
          )}
          {proposals.length > 0 && (
            <Section
              title={t('gaps.sectionPendingReview')}
              count={proposals.length}
              tone="amber"
              defaultOpen
            >
              {proposalGroups
                ? proposalGroups.map(({ priority, items }) => (
                    <PriorityBucket
                      key={priority}
                      priority={priority}
                      count={items.length}
                    >
                      {items.map((g) => (
                        <ProposalRow
                          key={g.card_id}
                          gap={g}
                          knownIds={knownIds}
                          onSelect={onSelect}
                          onApprove={onApproveProposal}
                          onReject={onRejectProposal}
                          onPeek={onPeek}
                          onDiscuss={onDiscussProposal}
                          onHoverTurns={onHoverTurns}
                          onJumpToTurn={onJumpToTurn}
                        />
                      ))}
                    </PriorityBucket>
                  ))
                : proposals.map((g) => (
                    <ProposalRow
                      key={g.card_id}
                      gap={g}
                      knownIds={knownIds}
                      onSelect={onSelect}
                      onApprove={onApproveProposal}
                      onReject={onRejectProposal}
                      onPeek={onPeek}
                      onDiscuss={onDiscussProposal}
                      onHoverTurns={onHoverTurns}
                      onJumpToTurn={onJumpToTurn}
                    />
                  ))}
            </Section>
          )}
          {staleCards.length > 0 && (
            <Section
              title={t('gaps.sectionStaleRipple')}
              count={staleCards.length}
              tone="ink"
              defaultOpen
            >
              {staleCards.map((c) => (
                <StaleRow key={c.card_id} card={c} onSelect={onSelect} />
              ))}
            </Section>
          )}
          {frameGaps.length > 0 && (
            <Section
              title={t('gaps.sectionFrameGaps')}
              count={frameGaps.length}
              tone="amber"
              defaultOpen
            >
              {frameGaps.map((g) => (
                <FrameGapRow
                  key={`${g.frameId}:${g.node.path}`}
                  gap={g}
                  onClick={onFrameGapClick}
                />
              ))}
            </Section>
          )}
        </div>
      )}
      {/* Bottom tips strip — same 48px band as the other three columns'
          footers, so the four columns line up across the page. Tips live
          here (rather than in the composer) because GapLedger has no
          active typing surface to compete with. */}
      <div
        style={{
          height: 48,
          padding: '0 12px',
          borderTop: '1px solid var(--rule)',
          background: 'var(--paper-1)',
          display: 'flex',
          alignItems: 'center',
          flexShrink: 0,
          boxSizing: 'border-box',
        }}
      >
        <TipsTicker />
      </div>
    </aside>
  );
}

// ── shell pieces ──────────────────────────────────────────────────

function Header({
  count,
  onCollapse,
  onAuditGaps,
  canAudit,
  auditing,
}: {
  count: number;
  onCollapse: () => void;
  onAuditGaps?: () => void;
  canAudit: boolean;
  auditing: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div
      className="mono"
      style={{
        height: 45,
        padding: '0 14px',
        borderBottom: '1px solid var(--rule)',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 10.5,
        color: count > 0 ? 'var(--red)' : 'var(--ink-3)',
        letterSpacing: '.12em',
        fontWeight: 700,
        flexShrink: 0,
        background: count > 0 ? 'var(--amber-soft)' : 'var(--paper-1)',
      }}
    >
      <span>◌ {t('gaps.heading')} · {count}</span>
      <span style={{ flex: 1 }} />
      {onAuditGaps && (
        <button
          onClick={onAuditGaps}
          disabled={!canAudit || auditing}
          title={
            !canAudit
              ? t('gaps.auditNeedsThread')
              : auditing
                ? t('gaps.auditInProgress')
                : t('gaps.auditTitle')
          }
          className="mono"
          style={{
            fontSize: 9.5,
            padding: '3px 8px',
            color: !canAudit || auditing ? 'var(--ink-3)' : 'var(--ink-1)',
            border: '1px solid var(--rule)',
            borderRadius: 3,
            background: 'white',
            cursor: !canAudit || auditing ? 'not-allowed' : 'pointer',
            letterSpacing: '.08em',
          }}
        >
          {auditing ? t('gaps.auditingBusy') : t('gaps.auditButton')}
        </button>
      )}
      <button
        onClick={onCollapse}
        title={t('gaps.hidePanel')}
        className="mono"
        style={{
          fontSize: 11,
          padding: '2px 6px',
          color: 'var(--ink-3)',
          border: '1px solid var(--rule)',
          borderRadius: 3,
          background: 'white',
          cursor: 'pointer',
        }}
      >
        ▸
      </button>
    </div>
  );
}

/** Read `extra.priority` defensively. Returns p0/p1/p2/p3 or null. */
function readPriority(card: Card): 'p0' | 'p1' | 'p2' | 'p3' | null {
  const p = (card.extra as Record<string, unknown> | null | undefined)?.priority;
  if (typeof p !== 'string') return null;
  if (p === 'p0' || p === 'p1' || p === 'p2' || p === 'p3') return p;
  return null;
}

/** Read `extra.source` for the chip (audit/decompose/rescue/chat/etc). */
function readSource(card: Card): string | null {
  const s = (card.extra as Record<string, unknown> | null | undefined)?.source;
  return typeof s === 'string' ? s : null;
}

interface PriorityGroup {
  priority: 'p0' | 'p1' | 'p2' | 'p3' | 'none';
  items: Card[];
}

/** Group proposals into p0/p1/p2/p3/none, dropping empty buckets, preserving
 *  intra-bucket order. */
function groupByPriority(proposals: Card[]): PriorityGroup[] {
  const buckets: Record<PriorityGroup['priority'], Card[]> = {
    p0: [],
    p1: [],
    p2: [],
    p3: [],
    none: [],
  };
  for (const p of proposals) {
    const k = readPriority(p) ?? 'none';
    buckets[k].push(p);
  }
  const order: PriorityGroup['priority'][] = ['p0', 'p1', 'p2', 'p3', 'none'];
  return order
    .filter((k) => buckets[k].length > 0)
    .map((priority) => ({ priority, items: buckets[priority] }));
}

/** i18n key per priority bucket — resolved at render time so a locale
 *  switch updates the labels. */
const PRIORITY_LABEL_KEY: Record<PriorityGroup['priority'], string> = {
  p0: 'gaps.priorityP0',
  p1: 'gaps.priorityP1',
  p2: 'gaps.priorityP2',
  p3: 'gaps.priorityP3',
  none: 'gaps.priorityNone',
};

const PRIORITY_COLOR: Record<PriorityGroup['priority'], string> = {
  p0: '#c1392b',
  p1: 'var(--amber, #b08a18)',
  p2: '#2c8a48',
  p3: 'var(--ink-3)',
  none: 'var(--ink-3)',
};

function PriorityBucket({
  priority,
  count,
  children,
}: {
  priority: PriorityGroup['priority'];
  count: number;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div style={{ marginBottom: 10 }}>
      <div
        className="mono"
        style={{
          fontSize: 9,
          letterSpacing: '.12em',
          fontWeight: 700,
          color: PRIORITY_COLOR[priority],
          padding: '2px 0 4px',
        }}
      >
        {t(PRIORITY_LABEL_KEY[priority])} · {count}
      </div>
      {children}
    </div>
  );
}

/**
 * Thin vertical rail shown when the gaps panel is hidden. Renders just the
 * GAPS count and an expand chevron, so the column reclaims ~250px of horizontal
 * space for the cards/discussion panes. Click anywhere on the rail to reopen.
 */
function CollapsedRail({ count, onExpand }: { count: number; onExpand: () => void }) {
  const { t } = useTranslation();
  return (
    <aside
      onClick={onExpand}
      title={count > 0 ? t('gaps.railExpandCount', { count }) : t('gaps.railExpand')}
      style={{
        width: COLLAPSED_WIDTH,
        flexShrink: 0,
        height: '100%',
        background: count > 0 ? 'var(--amber-soft)' : 'var(--paper-1)',
        borderLeft: '1px solid var(--rule)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        cursor: 'pointer',
      }}
    >
      <div
        className="mono"
        style={{
          height: 45,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 12,
          color: count > 0 ? 'var(--red)' : 'var(--ink-3)',
          width: '100%',
          borderBottom: '1px solid var(--rule)',
          fontWeight: 700,
        }}
      >
        ◀
      </div>
      <div
        className="mono"
        style={{
          marginTop: 12,
          writingMode: 'vertical-rl',
          transform: 'rotate(180deg)',
          fontSize: 10.5,
          letterSpacing: '.18em',
          color: count > 0 ? 'var(--red)' : 'var(--ink-3)',
          fontWeight: 700,
        }}
      >
        {t('gaps.heading')} · {count}
      </div>
    </aside>
  );
}

function EmptyState({
  onAuditGaps,
  canAudit,
  auditing,
}: {
  onAuditGaps?: () => void;
  canAudit: boolean;
  auditing: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px 18px',
        textAlign: 'center',
        gap: 14,
      }}
    >
      <div
        style={{
          fontSize: 11.5,
          color: 'var(--ink-3)',
          fontStyle: 'italic',
          lineHeight: 1.6,
          maxWidth: 220,
        }}
      >
        {t('gaps.emptyState')}
      </div>
      {onAuditGaps && canAudit && !auditing && (
        <button
          onClick={onAuditGaps}
          className="mono"
          style={{
            fontSize: 10,
            padding: '5px 12px',
            border: '1px solid var(--rule)',
            borderRadius: 3,
            background: 'white',
            cursor: 'pointer',
            color: 'var(--ink-1)',
            letterSpacing: '.08em',
          }}
        >
          {t('gaps.auditGapsButton')}
        </button>
      )}
    </div>
  );
}

interface SectionProps {
  title: string;
  count: number;
  tone: 'amber' | 'red' | 'ink';
  defaultOpen?: boolean;
  children: React.ReactNode;
}

function Section({ title, count, tone, defaultOpen = true, children }: SectionProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(defaultOpen);
  const color =
    tone === 'amber' ? 'var(--amber)' : tone === 'red' ? 'var(--red)' : 'var(--ink-2)';

  return (
    <div style={{ marginBottom: 14 }}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="mono"
        style={{
          width: '100%',
          textAlign: 'left',
          padding: '6px 4px',
          background: 'transparent',
          border: 'none',
          borderBottom: '1px solid var(--rule-s)',
          fontSize: 9.5,
          letterSpacing: '.12em',
          fontWeight: 700,
          color,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}
        title={open ? t('gaps.collapse') : t('gaps.expand')}
      >
        <span style={{ fontSize: 10, color: 'var(--ink-3)', width: 10 }}>
          {open ? '▾' : '▸'}
        </span>
        <span style={{ flex: 1 }}>{title} · {count}</span>
      </button>
      {open && <div style={{ padding: '6px 0 0' }}>{children}</div>}
    </div>
  );
}

// ── row variants ──────────────────────────────────────────────────

/**
 * OPEN QUESTIONS — a kind=question card the user has committed to track
 * (either approved from a gap proposal or written directly via write_card).
 * Two affordances:
 *   `[answer ↗]` — pins the question to a fresh thread + opens it. Same
 *      handler as Discuss (App routes both at `onDiscussProposal`); the
 *      App-side branch detects `kind=question` and skips the
 *      gap-specific opener.
 *   `[archive]`  — soft-hides via PATCH /archived. Card stays in the DB
 *      so wikilinks still resolve; just stops appearing here.
 *
 * Click anywhere else on the row opens CardPeek for inline editing /
 * delete (same wiring as the cards column's CardRow).
 */
function OpenQuestionRow({
  card,
  knownIds,
  onPeek,
  onSelect,
  onAnswer,
  onArchive,
}: {
  card: Card;
  knownIds: ReadonlySet<string>;
  onPeek?: (cardId: string, anchor: DOMRect) => void;
  onSelect: (id: string) => void;
  onAnswer?: (cardId: string) => void;
  onArchive?: (cardId: string) => void;
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState<null | 'archive'>(null);
  const display = card.title?.trim() || firstLine(card.body) || t('gaps.untitledQuestion');
  const handleArchive = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!onArchive || busy) return;
    setBusy('archive');
    try {
      await onArchive(card.card_id);
    } finally {
      setBusy(null);
    }
  };
  const handleAnswer = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!onAnswer) return;
    onAnswer(card.card_id);
  };
  return (
    <div
      style={{
        marginBottom: 10,
        padding: '8px 10px',
        // Distinct from amber proposals (agent-flavored) and red stale —
        // questions are user-owned settled-but-unresolved items, so a
        // muted ink rule reads as "your todo, not the agent's".
        borderLeft: '3px solid var(--ink-2)',
        background: 'white',
        border: '1px solid var(--rule-s)',
        borderLeftWidth: 3,
        borderRadius: 3,
        fontSize: 12,
        lineHeight: 1.45,
        cursor: 'pointer',
        opacity: busy ? 0.6 : 1,
      }}
      onClick={(e) => {
        if (onPeek) {
          const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
          onPeek(card.card_id, rect);
        } else {
          onSelect(card.card_id);
        }
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
        <span
          className="mono"
          style={{
            fontSize: 9.5,
            color: 'var(--ink-2)',
            letterSpacing: '.05em',
            fontWeight: 700,
          }}
        >
          ? {t('gaps.questionLabel')}
        </span>
        <span className="mono" style={{ fontSize: 9.5, color: 'var(--ink-3)' }}>
          {card.card_id}
        </span>
      </div>
      <div style={{ marginTop: 5, color: 'var(--ink-1)', fontWeight: 500 }}>
        {/* Inline markdown so `[[c-X]]` mentions inside a question body
            (e.g. "depends on the answer to [[c-7]]") render as clickable
            jump links. */}
        <CardBodyMarkdown
          body={display}
          knownIds={knownIds}
          onJumpToCard={(id, anchor) => {
            if (onPeek && anchor) {
              onPeek(id, anchor);
              return;
            }
            onSelect(id);
          }}
          inline
        />
      </div>
      <div
        style={{
          marginTop: 8,
          paddingTop: 6,
          borderTop: '1px dashed var(--rule)',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        <button
          onClick={handleAnswer}
          disabled={!onAnswer || busy !== null}
          className="mono"
          style={{
            fontSize: 10.5,
            padding: '3px 8px',
            border: '1px solid var(--ink-0)',
            borderRadius: 3,
            background: 'var(--ink-0)',
            color: 'white',
            fontWeight: 600,
            letterSpacing: '.04em',
            cursor: onAnswer && busy === null ? 'pointer' : 'not-allowed',
          }}
          title={t('gaps.answerTitle')}
        >
          {t('gaps.answer')} ↗
        </button>
        <span style={{ flex: 1 }} />
        <button
          onClick={handleArchive}
          disabled={!onArchive || busy !== null}
          className="mono"
          style={{
            fontSize: 10.5,
            padding: '3px 8px',
            border: '1px solid var(--rule)',
            borderRadius: 3,
            background: 'white',
            color: 'var(--ink-3)',
            fontWeight: 600,
            letterSpacing: '.04em',
            cursor: onArchive && busy === null ? 'pointer' : 'not-allowed',
          }}
          title={t('gaps.archiveTitle')}
        >
          {busy === 'archive' ? '…' : t('gaps.archive')}
        </button>
      </div>
    </div>
  );
}

/**
 * PENDING REVIEW — agent-proposed crystallization awaiting user resolution.
 * Click row → opens CardPeek in view mode (the peek's footer carries Edit,
 * which flips it into edit mode for "Save & Approve").
 *   Approve  → promote endpoint (gap → proposed_kind, same id). The
 *              button text reads `Add to OPEN` when proposed_kind=question
 *              (it'll join the OPEN QUESTIONS section above), and
 *              `Add to cards` for everything else (statement/decision/…).
 *   Discuss  → pin this gap to the DiscussionPane and steer the agent
 *              to resolve it.
 *   Dismiss  → reject_gap: deletes and writes dismissed_gaps marks.
 */
function ProposalRow({
  gap,
  knownIds,
  onSelect,
  onApprove,
  onReject,
  onPeek,
  onDiscuss,
  onHoverTurns,
  onJumpToTurn,
}: {
  gap: Card;
  /** Project-wide card ids — used to render `[[c-X]]` mentions inside the
   *  proposal body as clickable jump links. */
  knownIds: ReadonlySet<string>;
  onSelect: (id: string) => void;
  onApprove?: (cardId: string) => Promise<void> | void;
  onReject?: (cardId: string) => Promise<void> | void;
  onPeek?: (cardId: string, anchor: DOMRect) => void;
  onDiscuss?: (cardId: string) => void;
  onHoverTurns?: (turnIds: string[]) => void;
  onJumpToTurn?: (turnId: string) => void;
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState<null | 'approve' | 'reject'>(null);
  const blocks = stringArrayField(gap.extra, 'blocks');
  const literalProposedKind =
    stringField(gap.extra, 'proposed_kind') ??
    stringField(gap.extra, 'proposes_kind');
  const ambiguity = stringField(gap.extra, 'ambiguity_reason');
  const display = gap.title?.trim() || firstLine(gap.body) || t('gaps.untitledProposal');
  // Conflict gaps come from the cross-thread detector and are resolved by
  // editing one of the two `blocks` cards (or rejecting if it's a false
  // positive) — Approve/promote doesn't make sense for them. We hide the
  // Approve button so the only actions are Discuss + Reject.
  const gapKind = stringField(gap.extra, 'gap_kind');
  const isConflict = gapKind === 'conflict';
  // Effective destination kind — mirrors the server's promote fallback in
  // `card_ops::default_target_kind_for_legacy_gap`. Legacy gaps written
  // before the unified-gap redesign carry only `gap_kind` (no
  // `proposed_kind`); on Approve, server lands them as `statement` (when
  // gap_kind=conflict) or `question` (everything else). The chip and the
  // button both honor this so the user can see the real destination
  // before clicking.
  const proposedKind =
    literalProposedKind ?? (gapKind === 'conflict' ? 'statement' : 'question');
  // Citations the agent recorded — extra.context.{turns,cards,refs}.
  // Surfaced as a footer ("cited: t-42, t-44 · c-12") and used to drive the
  // DiscussionPane highlight on hover.
  const contextNode = (gap.extra?.['context'] as Record<string, unknown> | undefined) ?? {};
  const citedTurns = Array.isArray(contextNode['turns'])
    ? (contextNode['turns'] as unknown[]).filter((t): t is string => typeof t === 'string')
    : [];
  const citedCards = Array.isArray(contextNode['cards'])
    ? (contextNode['cards'] as unknown[]).filter((t): t is string => typeof t === 'string')
    : [];
  const citedRefs = Array.isArray(contextNode['refs'])
    ? (contextNode['refs'] as unknown[]).filter((t): t is string => typeof t === 'string')
    : [];

  const handleApprove = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!onApprove || busy) return;
    setBusy('approve');
    try {
      await onApprove(gap.card_id);
    } finally {
      setBusy(null);
    }
  };
  const handleReject = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!onReject || busy) return;
    setBusy('reject');
    try {
      await onReject(gap.card_id);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      style={{
        marginBottom: 10,
        padding: '8px 10px',
        borderLeft: '3px solid var(--amber)',
        background: 'white',
        border: '1px solid var(--rule-s)',
        borderLeftWidth: 3,
        borderRadius: 3,
        fontSize: 12,
        lineHeight: 1.45,
        cursor: 'pointer',
        opacity: busy ? 0.6 : 1,
      }}
      onClick={(e) => {
        if (onPeek) {
          const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
          onPeek(gap.card_id, rect);
        } else {
          onSelect(gap.card_id);
        }
      }}
      onMouseEnter={() => {
        if (onHoverTurns && citedTurns.length > 0) onHoverTurns(citedTurns);
      }}
      onMouseLeave={() => {
        if (onHoverTurns && citedTurns.length > 0) onHoverTurns([]);
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
        <span
          className="mono"
          style={{
            fontSize: 9.5,
            color: 'var(--amber)',
            letterSpacing: '.05em',
            fontWeight: 700,
            background: 'var(--amber-soft)',
            padding: '1px 5px',
            borderRadius: 2,
            border: '1px solid var(--amber)',
          }}
        >
          ✦ {proposedKind}
        </span>
        {(() => {
          const p = readPriority(gap);
          return p ? <RowPriorityChip priority={p} /> : null;
        })()}
        {(() => {
          const s = readSource(gap);
          return s ? <RowSourceChip source={s} /> : null;
        })()}
        <span className="mono" style={{ fontSize: 9.5, color: 'var(--ink-3)' }}>
          {gap.card_id}
        </span>
      </div>
      <div style={{ marginTop: 5, color: 'var(--ink-1)', fontWeight: 500 }}>
        {/* `display` is normally a single-line title; falls back to the
            body's first line when title is empty. Render via CardBodyMarkdown
            so `[[c-X]]` mentions inside the body fallback (e.g. "已定义的部分:
            [[c-80]] — …") become clickable. `inline=true` strips the wrapping
            <p> so the row's flexbox rhythm stays intact. Click routes
            through `onPeek` (opens CardPeek anchored to the link rect) when
            wired, falling back to a plain select. */}
        <CardBodyMarkdown
          body={display}
          knownIds={knownIds}
          onJumpToCard={(id, anchor) => {
            if (onPeek && anchor) {
              onPeek(id, anchor);
              return;
            }
            onSelect(id);
          }}
          inline
        />
      </div>
      {ambiguity && (
        <div
          style={{
            marginTop: 4,
            fontSize: 11,
            color: 'var(--ink-3)',
            fontStyle: 'italic',
            lineHeight: 1.4,
          }}
        >
          {t('gaps.whyPrefix')} {ambiguity}
        </div>
      )}
      {blocks.length > 0 && <BlocksChips blocks={blocks} onSelect={onSelect} />}
      {(citedTurns.length > 0 || citedCards.length > 0 || citedRefs.length > 0) && (
        <div
          className="mono"
          style={{
            marginTop: 6,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 6,
            alignItems: 'baseline',
            fontSize: 9.5,
            color: 'var(--ink-3)',
            letterSpacing: '.02em',
          }}
          title={t('gaps.citedTitle')}
        >
          <span style={{ letterSpacing: '.06em', fontWeight: 700 }}>{t('gaps.citedLabel')}</span>
          {citedTurns.length > 0 && (
            <span style={{ color: 'var(--amber)', display: 'inline-flex', flexWrap: 'wrap', gap: 4 }}>
              {citedTurns.map((tid, i) => (
                <span key={tid}>
                  {onJumpToTurn ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onJumpToTurn(tid);
                      }}
                      className="mono"
                      title={t('gaps.jumpToTurn', { turn: tid })}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        margin: 0,
                        font: 'inherit',
                        color: 'inherit',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        textUnderlineOffset: 2,
                        textDecorationColor: 'var(--amber)',
                        textDecorationThickness: 1,
                      }}
                    >
                      {tid}
                    </button>
                  ) : (
                    tid
                  )}
                  {i < citedTurns.length - 1 ? ', ' : ''}
                </span>
              ))}
            </span>
          )}
          {citedCards.length > 0 && (
            <span style={{ color: 'var(--ink-2)' }}>
              · {citedCards.join(', ')}
            </span>
          )}
          {citedRefs.length > 0 && (
            <span style={{ color: 'var(--ink-2)' }}>
              · {t('gaps.refsLabel')}&nbsp;{citedRefs.join(', ')}
            </span>
          )}
        </div>
      )}
      <div
        style={{
          marginTop: 8,
          paddingTop: 6,
          borderTop: '1px dashed var(--rule)',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        {!isConflict && (
          <button
            onClick={handleApprove}
            disabled={!onApprove || busy !== null}
            className="mono"
            style={{
              fontSize: 10.5,
              padding: '3px 8px',
              border: '1px solid var(--ink-0)',
              borderRadius: 3,
              background: 'var(--ink-0)',
              color: 'white',
              fontWeight: 600,
              letterSpacing: '.04em',
              cursor: busy === null && onApprove ? 'pointer' : 'not-allowed',
            }}
            title={
              proposedKind === 'question'
                ? t('gaps.approveTitleQuestion')
                : t('gaps.approveTitleCard', { kind: proposedKind })
            }
          >
            {busy === 'approve'
              ? '…'
              : proposedKind === 'question'
                ? t('gaps.approveToOpen')
                : t('gaps.approveToCards')}
          </button>
        )}
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (onDiscuss) onDiscuss(gap.card_id);
          }}
          disabled={busy !== null || !onDiscuss}
          className="mono"
          style={{
            fontSize: 10.5,
            padding: '3px 8px',
            border: '1px solid var(--amber)',
            borderRadius: 3,
            background: 'var(--amber-soft)',
            color: 'var(--amber)',
            fontWeight: 600,
            letterSpacing: '.04em',
            cursor: busy === null && onDiscuss ? 'pointer' : 'not-allowed',
          }}
          title={t('gaps.discussTitle')}
        >
          {t('gaps.discuss')}
        </button>
        <span style={{ flex: 1 }} />
        <button
          onClick={handleReject}
          disabled={!onReject || busy !== null}
          className="mono"
          style={{
            fontSize: 10.5,
            padding: '3px 8px',
            border: '1px solid var(--red)',
            borderRadius: 3,
            background: 'white',
            color: 'var(--red)',
            fontWeight: 600,
            letterSpacing: '.04em',
            cursor: busy === null && onReject ? 'pointer' : 'not-allowed',
          }}
          title={t('gaps.dismissTitle')}
        >
          {busy === 'reject' ? '…' : t('gaps.dismiss')}
        </button>
      </div>
    </div>
  );
}

/** FRAME GAPS — empty `mece_required` nodes in the active frame. Clicking
 *  one opens the same probe popover that the inline MECE banner does. */
function FrameGapRow({
  gap,
  onClick,
}: {
  gap: FrameGapInfo;
  onClick?: (gap: FrameGapInfo) => void;
}) {
  const { t } = useTranslation();
  const probeCount = gap.node.probes?.length ?? 0;
  return (
    <div
      style={{
        marginBottom: 8,
        padding: '8px 10px',
        borderLeft: '2px solid var(--amber)',
        background: 'white',
        border: '1px solid var(--rule-s)',
        borderLeftWidth: 2,
        borderRadius: 3,
        fontSize: 12,
        lineHeight: 1.45,
        cursor: onClick ? 'pointer' : 'default',
      }}
      onClick={() => onClick?.(gap)}
      title={
        probeCount > 0
          ? t('gaps.openProbeCount', { count: probeCount })
          : t('gaps.openProbePopover')
      }
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
        <span
          className="mono"
          style={{
            fontSize: 9.5,
            color: 'var(--amber)',
            letterSpacing: '.05em',
            fontWeight: 700,
            background: 'var(--amber-soft)',
            padding: '1px 5px',
            borderRadius: 2,
            border: '1px solid var(--amber)',
          }}
        >
          ▢ {t('gaps.frameLabel')}
        </span>
        <span className="mono" style={{ fontSize: 9.5, color: 'var(--ink-3)' }}>
          {gap.frameName}
        </span>
      </div>
      <div style={{ marginTop: 5, color: 'var(--ink-1)', fontWeight: 500 }}>
        {t('gaps.nodeEmpty', { label: gap.node.label })}
      </div>
      {gap.node.description && (
        <div
          style={{
            marginTop: 4,
            fontSize: 11,
            color: 'var(--ink-3)',
            fontStyle: 'italic',
            lineHeight: 1.4,
          }}
        >
          {gap.node.description}
        </div>
      )}
    </div>
  );
}

/** STALE RIPPLE — non-gap cards with stale=true. */
function StaleRow({ card, onSelect }: { card: Card; onSelect: (id: string) => void }) {
  const { t } = useTranslation();
  const display = card.title?.trim() || firstLine(card.body) || t('gaps.untitled');
  return (
    <div
      style={{
        marginBottom: 8,
        padding: '7px 10px',
        borderLeft: '2px solid var(--ink-2)',
        background: 'white',
        border: '1px solid var(--rule-s)',
        borderLeftWidth: 2,
        borderRadius: 3,
        fontSize: 12,
        lineHeight: 1.45,
        cursor: 'pointer',
      }}
      onClick={() => onSelect(card.card_id)}
      title={t('gaps.openCard')}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
        <span
          className="mono"
          style={{
            fontSize: 9.5,
            color: 'var(--ink-2)',
            letterSpacing: '.05em',
            fontWeight: 700,
          }}
        >
          △ {card.kind}
        </span>
        <span className="mono" style={{ fontSize: 9.5, color: 'var(--ink-3)' }}>
          {card.card_id}
        </span>
      </div>
      <div
        style={{
          marginTop: 4,
          color: 'var(--ink-2)',
          textDecoration: 'line-through',
          textDecorationColor: 'var(--ink-3)',
        }}
      >
        {display}
      </div>
    </div>
  );
}

function BlocksChips({
  blocks,
  onSelect,
}: {
  blocks: string[];
  onSelect: (id: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div
      className="mono"
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 4,
        marginTop: 6,
        fontSize: 10,
        color: 'var(--ink-2)',
      }}
    >
      <span style={{ color: 'var(--ink-3)', letterSpacing: '.04em' }}>{t('gaps.blocksLabel')}</span>
      {blocks.map((id) => (
        <button
          key={id}
          onClick={(e) => {
            e.stopPropagation();
            onSelect(id);
          }}
          className="mono"
          style={{
            fontSize: 10,
            padding: '0 4px',
            border: '1px solid var(--rule)',
            borderRadius: 2,
            background: 'var(--paper-0)',
            color: 'var(--ink-1)',
            cursor: 'pointer',
          }}
          title={t('gaps.jumpTo', { id })}
        >
          [[{id}]]
        </button>
      ))}
    </div>
  );
}

// ── helpers ───────────────────────────────────────────────────────

function stringField(extra: Record<string, unknown>, key: string): string | null {
  const v = extra?.[key];
  return typeof v === 'string' && v.length > 0 ? v : null;
}

function stringArrayField(extra: Record<string, unknown>, key: string): string[] {
  const v = extra?.[key];
  if (!Array.isArray(v)) return [];
  return v.filter((x): x is string => typeof x === 'string' && x.length > 0);
}

function firstLine(s: string): string {
  const first = s.split('\n').find((l) => l.trim().length > 0);
  return first?.trim() ?? '';
}

/** Inline priority chip on a row. Smaller than the bucket header; the
 *  primary signal is grouping by priority — the chip is reinforcement so
 *  rows aren't visually anonymous when extracted from their bucket. */
function RowPriorityChip({ priority }: { priority: 'p0' | 'p1' | 'p2' | 'p3' }) {
  const palette = {
    p0: { bg: 'rgba(220,53,69,.12)', fg: '#c1392b' },
    p1: { bg: 'rgba(212,160,23,.15)', fg: 'var(--amber, #b08a18)' },
    p2: { bg: 'rgba(46,160,80,.12)', fg: '#2c8a48' },
    p3: { bg: 'rgba(0,0,0,.06)', fg: 'var(--ink-3)' },
  }[priority];
  return (
    <span
      className="mono"
      style={{
        fontSize: 8.5,
        fontWeight: 700,
        letterSpacing: '.06em',
        padding: '1px 4px',
        borderRadius: 2,
        background: palette.bg,
        color: palette.fg,
      }}
    >
      {priority.toUpperCase()}
    </span>
  );
}

/** Source chip — where the gap came from. `audit` and `decompose` from the
 *  named distill modes; `chat` for opportunistic mid-conversation
 *  propose_card calls; `rescue` for the original distill flow. Unknown
 *  sources render the literal string so additions are forward-compatible. */
function RowSourceChip({ source }: { source: string }) {
  const known: Record<string, { bg: string; fg: string; label: string }> = {
    audit: { bg: 'rgba(99,102,241,.10)', fg: '#4f46e5', label: 'AUDIT' },
    decompose: { bg: 'rgba(99,102,241,.08)', fg: '#6366f1', label: 'DECOMPOSE' },
    rescue: { bg: 'rgba(0,0,0,.04)', fg: 'var(--ink-3)', label: 'RESCUE' },
    chat: { bg: 'rgba(0,0,0,.04)', fg: 'var(--ink-3)', label: 'CHAT' },
  };
  const palette = known[source] ?? {
    bg: 'rgba(0,0,0,.04)',
    fg: 'var(--ink-3)',
    label: source.toUpperCase(),
  };
  return (
    <span
      className="mono"
      style={{
        fontSize: 8.5,
        fontWeight: 600,
        letterSpacing: '.06em',
        padding: '1px 4px',
        borderRadius: 2,
        background: palette.bg,
        color: palette.fg,
      }}
    >
      {palette.label}
    </span>
  );
}
